import { Link } from 'react-router-dom';
import { services, serviceCategories } from '@/mocks/services';

const Footer = () => {
  const scrollToSection = (id: string) => {
    if (window.location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-brand-charcoal text-white pt-16 pb-6 rounded-t-3xl">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-white/10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src="https://static.readdy.ai/image/2733261fde4eb6ca5d5b92336d650a8a/d408af891b414e81de7eede75195e752.png"
              alt="Valley View Construction"
              className="h-14 w-auto object-contain mb-4"
            />
            <p className="text-sm text-white/60 leading-relaxed mb-5">
              Premier masonry and construction in Elkhart, Indiana. Serving all Michiana within 30 miles with craftsmanship that lasts generations.
            </p>
            {/* Social */}
            <div>
              <p className="text-xs font-semibold text-brand-teal uppercase tracking-wider mb-3">Follow Us</p>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/8 hover:bg-brand-teal rounded-lg text-white/80 hover:text-white transition-all duration-200 text-sm font-medium"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-facebook-fill" />
                </div>
                Follow on Facebook
              </a>
            </div>
          </div>

          {/* Services Column 1 */}
          <div>
            <h4 id="footer-masonry">
              <a href="#masonry" className="text-xs font-semibold text-brand-teal uppercase tracking-wider mb-4 block">Masonry & Outdoor</a>
            </h4>
            <ul className="space-y-2">
              {['masonry-stonework', 'chimney-construction', 'stone-steps-borders', 'hardscape-patios', 'outdoor-kitchens', 'walkways-driveways'].map((id) => {
                const svc = services.find((s) => s.id === id);
                if (!svc) return null;
                return (
                  <li key={id}>
                    <Link
                      to={`/services/${id}`}
                      className="text-sm text-white/60 hover:text-brand-teal-light transition-colors flex items-center gap-2"
                    >
                      <i className="ri-arrow-right-s-line text-brand-teal/50" />
                      {svc.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Services Column 2 */}
          <div>
            <h4 id="footer-construction">
              <a href="#construction" className="text-xs font-semibold text-brand-teal uppercase tracking-wider mb-4 block">Construction & More</a>
            </h4>
            <ul className="space-y-2 mb-6">
              {['retaining-walls', 'fencing', 'landscaping', 'home-remodeling', 'commercial-construction', 'indoor-kitchens'].map((id) => {
                const svc = services.find((s) => s.id === id);
                if (!svc) return null;
                return (
                  <li key={id}>
                    <Link
                      to={`/services/${id}`}
                      className="text-sm text-white/60 hover:text-brand-teal-light transition-colors flex items-center gap-2"
                    >
                      <i className="ri-arrow-right-s-line text-brand-teal/50" />
                      {svc.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 id="footer-contact">
              <a href="#contact" className="text-xs font-semibold text-brand-teal uppercase tracking-wider mb-4 block">Contact Us</a>
            </h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:+15744096118" className="flex items-start gap-3 group">
                  <div className="w-8 h-8 flex items-center justify-center bg-brand-teal/15 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-brand-teal text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 mb-0.5">Phone</p>
                    <p className="text-sm font-semibold text-white group-hover:text-brand-teal-light transition-colors">
                      574-409-6118
                    </p>
                  </div>
                </a>
              </li>
              <li>
                <a href="mailto:valleyviewconstruction98@gmail.com" className="flex items-start gap-3 group">
                  <div className="w-8 h-8 flex items-center justify-center bg-brand-teal/15 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-brand-teal text-sm" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 mb-0.5">Email</p>
                    <p className="text-xs font-medium text-white/80 group-hover:text-brand-teal-light transition-colors break-all">
                      valleyviewconstruction98@gmail.com
                    </p>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-brand-teal/15 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-brand-teal text-sm" />
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Location</p>
                  <p className="text-sm text-white/80">Elkhart, Indiana</p>
                  <p className="text-xs text-white/50">Serving all Michiana</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-brand-teal/15 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-brand-teal text-sm" />
                </div>
                <div>
                  <p className="text-xs text-white/40 mb-0.5">Hours</p>
                  <p className="text-sm text-white/80">Mon–Fri: 7am–6pm</p>
                  <p className="text-sm text-white/80">Sat: 8am–4pm</p>
                  <p className="text-xs text-white/40">Sun: Closed</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Service Area Badge */}
        <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-white/10">
          <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
            {['Elkhart', 'Goshen', 'Mishawaka', 'South Bend', 'Granger', 'Nappanee', 'Bristol', 'Middlebury', 'Osceola', 'Dunlap'].map((city) => (
              <span key={city} className="px-3 py-1 text-xs bg-white/8 text-white/60 rounded-full">
                {city}, IN
              </span>
            ))}
            <span className="px-3 py-1 text-xs bg-brand-amber/20 text-brand-amber rounded-full font-medium">
              + 30-Mile Radius
            </span>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40 text-center sm:text-left">
            © 2026 Valley View Construction. All rights reserved. · Elkhart, Indiana
          </p>
          <div className="flex items-center gap-4">
            <button
              onClick={() => scrollToSection('contact')}
              className="text-xs text-white/40 hover:text-brand-amber transition-colors cursor-pointer"
            >
              Privacy Policy
            </button>
            <span className="text-white/20">|</span>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-xs text-white/40 hover:text-brand-amber transition-colors cursor-pointer"
            >
              Terms of Service
            </button>
            <span className="text-white/20">|</span>
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="nofollow noreferrer"
              className="text-xs text-white/40 hover:text-brand-amber transition-colors"
            >
              Facebook
            </a>
          </div>
        </div>


      </div>

      {/* Melt Red Bar */}
      <div className="w-full py-3 flex items-center justify-center relative overflow-hidden">
        {/* Fade left */}
        <div className="absolute left-0 top-0 h-full w-32 bg-gradient-to-r from-brand-charcoal to-transparent pointer-events-none z-10" />
        {/* Fade right */}
        <div className="absolute right-0 top-0 h-full w-32 bg-gradient-to-l from-brand-charcoal to-transparent pointer-events-none z-10" />
        {/* Red pill */}
        <a
          href="https://meltgraphicsprint.com/"
          target="_blank"
          rel="nofollow noreferrer"
          className="relative z-20 flex items-center gap-3 bg-red-600 hover:bg-red-700 transition-colors px-8 py-2 rounded-full cursor-pointer"
        >
          <span className="text-white text-xs font-semibold tracking-wide whitespace-nowrap">
            Site Powered &amp; Copyrighted by
          </span>
          <img
            src="https://storage.readdy-site.link/project_files/46b5028d-8e1d-4b10-b368-77259c0a6540/992595fe-c7ad-4431-8154-870380b01e75_MELT_logo.png?v=ae697db432914775fa306a0b4ef0e023"
            alt="Melt Graphics"
            className="h-5 w-auto object-contain"
          />
        </a>
      </div>
    </footer>
  );
};

export default Footer;
